<script lang="ts" setup>
import { computed, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import ControlCustomerPageTab from '../components/ControlCustomerPageTab.vue';
import { CUSTOMER_TAB_ROUTER_INFO } from '../constants';
import NewBaseTableContent from '@/corePagesModule/BaseTableContent/NewBaseTableContent.vue';
import MonthPicker from '@/corePagesModule/components/MonthPicker.vue';
import Pagination from '@/corePagesModule/components/Pagination.vue';
import SelectInput from '@/corePagesModule/SelectInput';
import { i18n } from '@/i18n';
import MultipleUserSelectInput from '@/corePagesModule/user/SelectInput/MultipleUserSelectInput.vue';
import { useForecastAccuracyStore } from './useForecastAccuracyStore';
import DownloadAccuracyRateModal from '../components/DownloadAccuracyRateModal.vue';

const t = i18n.t;
const locale = computed(() => i18n.locale);

// Use store
const store = useForecastAccuracyStore();
const { state, tableContentItems, monthsOptionsDisplayForecast, tableHeaders } = storeToRefs(store);

// Destructure store methods for easier access
const {
  onSelectedReceiveMonth,
  onChangePaging,
  onSelectedDisplayForecastsMonth,
  updateResponsibleUserFilter,
  onClickDownload,
  sortTableContentItems,
} = store;

// Initialize store on mount
onMounted(() => {
  store.init();
});
</script>

<template>
  <div class="forecast-accuracy">
    <div class="forecast-accuracy__top-area top-area">
      <ControlCustomerPageTab class="top-area__tab-bar" :tab-info-object="[...CUSTOMER_TAB_ROUTER_INFO]" />
    </div>
    <div class="forecast-accuracy__filter-content filter-content">
      <!-- 受領用内示担当者の選択用UI -->
      <MultipleUserSelectInput
        class="filter-content__select-responsible-user"
        :select-input-index="31"
        :label="''"
        :original-selected-user-id-list="state.selectedResponsibleUserIdList"
        @update:model-value="(userIds) => updateResponsibleUserFilter(userIds)"
      />
    </div>
    <div class="forecast-accuracy__table-area table-area">
      <div class="table-area__top-accessory">
        <div class="table-area__select-month-area select-month-area">
          <div class="select-month-area__title">
            {{ locale === 'ja' ? '表示月' : 'Display Month' }}
          </div>
          <MonthPicker
            class="select-month-area__calendar"
            :model-value="state.receivedForecastYearMonthInfo"
            :locale="locale"
            :prop-format="t('MonthPickerFormat')"
            @update:model-value="onSelectedReceiveMonth($event)"
          />
        </div>
        <Pagination
          v-if="state.tableContents.pagingOptions"
          :paging-options="state.tableContents.pagingOptions"
          :is-loading="state.tableContents.loadingState.status.type === 'loading'"
          class="table-area__pagination"
          @on-change-page="onChangePaging($event)"
        />
        <SelectInput
          class="table-area__forecast-length-select"
          :index="30"
          :model-value="t('displayMonthsOfData', { month: state.displayForecastsMonth })"
          :placeholder="''"
          :label="''"
          :filter-type="'DISPLAY_MONTH'"
          :prop-options="monthsOptionsDisplayForecast"
          :is-open-prop="state.openedSelectInputType === 'DISPLAY_MONTH'"
          use-teleport
          @click:select-input="() => (state.openedSelectInputType = 'DISPLAY_MONTH')"
          @update:value-with-index="(_, index) => onSelectedDisplayForecastsMonth(index)"
        />
      </div>
      <div
        v-if="state.tableContents.loadingState.status.type === 'zeroMatch'"
        class="table-area__business-table table-area__business-table--zero-match"
      >
        {{ t('forecastForReceipt_placeholderForZeroMatch') }}
      </div>
      <NewBaseTableContent
        v-else
        :key="state.tableContents.renderingKey"
        class="table-area__business-table table-area__business-table--success"
        :prop-table-content-properties="tableHeaders"
        :prop-display-objects="tableContentItems"
        :prop-loading-status="state.tableContents.loadingState"
        :prop-table-type="'forecastAccuracyTable'"
        @on-click-sort="(sortKey) => sortTableContentItems(sortKey)"
        @on-click-download="(displayId) => onClickDownload(displayId)"
      />
    </div>

    <!-- Download Modal -->
    <DownloadAccuracyRateModal
      :store="store"
      :default-start-year-month="state.downloadModalState.startYearMonth"
      :default-end-year-month="state.downloadModalState.endYearMonth"
    />
  </div>
</template>

<style lang="scss" scoped>
.forecast-accuracy {
  width: 100%;
  min-height: 100%;
  max-height: 100%;
  display: grid;
  grid-template-rows: repeat(2, auto) minmax(0, 1fr);
  gap: 8px;
  font: Noto Sans JP;
  letter-spacing: 0;
  white-space: nowrap;

  .top-area {
    display: flex;
    align-items: start;
    justify-content: start;
  }

  .filter-content {
    display: flex;
    align-items: center;
    gap: 16px;

    &__select-responsible-user {
      width: 200px;
    }
  }

  .select-month-area {
    display: grid;
    grid-template-columns: repeat(2, auto);
    justify-content: start;
    align-items: center;
    gap: 8px;
    padding-bottom: 8px;

    &__title {
      font-size: 14px;
      line-height: 20.27px;
      font-weight: 400;
      color: $textDefault;
    }

    &__calendar {
      width: 141px;
      height: 36px;
      background-color: $semanticWhite;
      border-radius: 4px;
      border: 1px solid $borderDefault;
      display: grid;
      place-items: center;
    }
  }

  .table-area {
    height: 100%;
    overflow-x: auto;
    display: grid;
    grid-template-rows: auto minmax(0, 1fr);

    &__top-accessory {
      display: grid;
      grid-template-columns: repeat(3, auto);
      justify-content: space-between;
      align-items: center;
      gap: 8px;
    }

    &__business-table {
      &--zero-match {
        background-color: $surfaceDefault;
        display: grid;
        place-items: center;
        font-weight: 400;
        font-size: 14px;
        line-height: 18.2px;
        color: $textSecondary;
      }
      &--success {
        overflow-y: scroll !important;
        background-color: $backgroundDefault;
      }
      border: 1px solid $borderDefault;
      min-height: 0 !important;
      max-height: 100% !important;
    }

    &__forecast-length-select {
      width: 162px;
      height: 32px;
    }
  }
}
</style>

<style lang="scss">
.select-month-area__calendar input {
  font-family: Noto Sans JP;
  width: 100%;
  font-size: 16px;
  line-height: 16px;
  font-weight: 700;
  color: $textDefault;
}
</style>
